import { hexToBytes, joinSegments } from './dtc.ts';

// Mode 01 live data. Only the handful worth showing on a scan summary; the
// full PID list runs to hundreds and most are not useful without a graph.

export interface PidSpec {
  pid: string;
  key: string;
  label: string;
  unit: string;
  /** Bytes after the "41 xx" echo, decoded to a display value. */
  decode: (bytes: number[]) => number | null;
  /** Sensible display precision. */
  precision?: number;
}

export const PIDS: PidSpec[] = [
  {
    pid: '010C',
    key: 'rpm',
    label: 'Engine speed',
    unit: 'rpm',
    decode: (b) => (b.length >= 2 ? (b[0] * 256 + b[1]) / 4 : null),
  },
  {
    pid: '010D',
    key: 'speedKph',
    label: 'Vehicle speed',
    unit: 'km/h',
    decode: (b) => (b.length >= 1 ? b[0] : null),
  },
  {
    pid: '0105',
    key: 'coolantC',
    label: 'Coolant',
    unit: '°C',
    decode: (b) => (b.length >= 1 ? b[0] - 40 : null),
  },
  {
    pid: '010F',
    key: 'intakeC',
    label: 'Intake air',
    unit: '°C',
    decode: (b) => (b.length >= 1 ? b[0] - 40 : null),
  },
  {
    pid: '0104',
    key: 'loadPct',
    label: 'Engine load',
    unit: '%',
    decode: (b) => (b.length >= 1 ? (b[0] * 100) / 255 : null),
    precision: 0,
  },
  {
    pid: '012F',
    key: 'fuelPct',
    label: 'Fuel level',
    unit: '%',
    decode: (b) => (b.length >= 1 ? (b[0] * 100) / 255 : null),
    precision: 0,
  },
  {
    pid: '0142',
    key: 'voltage',
    label: 'Module voltage',
    unit: 'V',
    decode: (b) => (b.length >= 2 ? (b[0] * 256 + b[1]) / 1000 : null),
    precision: 1,
  },
  // Everything below was reachable all along and never asked for.
  {
    pid: '0110',
    key: 'mafGs',
    label: 'MAF rate',
    unit: 'g/s',
    decode: (b) => (b.length >= 2 ? (b[0] * 256 + b[1]) / 100 : null),
    precision: 2,
  },
  {
    pid: '0106',
    key: 'stftB1',
    label: 'Short trim B1',
    unit: '%',
    // Fuel trims are signed around a 128 midpoint: negative is pulling fuel out.
    decode: (b) => (b.length >= 1 ? (b[0] - 128) * (100 / 128) : null),
    precision: 1,
  },
  {
    pid: '0107',
    key: 'ltftB1',
    label: 'Long trim B1',
    unit: '%',
    decode: (b) => (b.length >= 1 ? (b[0] - 128) * (100 / 128) : null),
    precision: 1,
  },
  {
    pid: '0111',
    key: 'throttlePct',
    label: 'Throttle',
    unit: '%',
    decode: (b) => (b.length >= 1 ? (b[0] * 100) / 255 : null),
    precision: 0,
  },
  {
    pid: '010E',
    key: 'timingAdv',
    label: 'Timing advance',
    unit: '°',
    decode: (b) => (b.length >= 1 ? b[0] / 2 - 64 : null),
    precision: 1,
  },
  {
    pid: '010B',
    key: 'mapKpa',
    label: 'Intake MAP',
    unit: 'kPa',
    decode: (b) => (b.length >= 1 ? b[0] : null),
    precision: 0,
  },
  {
    pid: '011F',
    key: 'runTimeS',
    label: 'Run time',
    unit: 's',
    decode: (b) => (b.length >= 2 ? b[0] * 256 + b[1] : null),
    precision: 0,
  },
  {
    pid: '0121',
    key: 'distMilKm',
    label: 'Distance with MIL',
    unit: 'km',
    decode: (b) => (b.length >= 2 ? b[0] * 256 + b[1] : null),
    precision: 0,
  },
  {
    pid: '0131',
    key: 'distClearedKm',
    label: 'Distance since clear',
    unit: 'km',
    // Small values here mean somebody wiped the codes very recently.
    decode: (b) => (b.length >= 2 ? b[0] * 256 + b[1] : null),
    precision: 0,
  },
  {
    pid: '015C',
    key: 'oilTempC',
    label: 'Oil temp',
    unit: '°C',
    decode: (b) => (b.length >= 1 ? b[0] - 40 : null),
    precision: 0,
  },
  {
    pid: '0146',
    key: 'ambientC',
    label: 'Ambient air',
    unit: '°C',
    decode: (b) => (b.length >= 1 ? b[0] - 40 : null),
    precision: 0,
  },
  {
    pid: '0133',
    key: 'baroKpa',
    label: 'Barometric',
    unit: 'kPa',
    decode: (b) => (b.length >= 1 ? b[0] : null),
    precision: 0,
  },
];

/** PID number as an integer, for matching against the supported-PID set. */
export function pidNumber(spec: PidSpec): number {
  return parseInt(spec.pid.slice(2), 16);
}

export type Vitals = Record<string, number | null>;

/** 1 is live data (service $01), 2 is freeze frame (service $02). */
export type PidMode = 1 | 2;

/**
 * Build the request for a PID in either mode.
 *
 * Mode 02 takes a frame number after the PID. Every production vehicle stores
 * exactly one frame, so it is always 00, but the byte is not optional.
 */
export function pidRequest(spec: PidSpec, mode: PidMode = 1): string {
  const pid = spec.pid.slice(2).toUpperCase();
  return mode === 1 ? `01${pid}` : `02${pid}00`;
}

/**
 * Decode a PID reply.
 *
 * The response echoes the request with the mode raised by 0x40, so 010C comes
 * back as 410C. Mode 02 then inserts the frame number between the PID echo and
 * the data, which is the one difference that matters: reading a freeze frame
 * with the mode 01 offset decodes the frame number as the first data byte and
 * silently returns a wrong value rather than failing.
 */
export function parsePidResponse(raw: string, spec: PidSpec, mode: PidMode = 1): number | null {
  const hex = joinSegments(raw);
  const echo = (0x40 + mode).toString(16).toUpperCase().padStart(2, '0') + spec.pid.slice(2).toUpperCase();
  const at = hex.indexOf(echo);
  if (at < 0) return null;

  const frameByteLength = mode === 2 ? 2 : 0;
  const value = spec.decode(hexToBytes(hex.slice(at + echo.length + frameByteLength)));
  if (value === null || !Number.isFinite(value)) return null;
  const precision = spec.precision ?? (Number.isInteger(value) ? 0 : 1);
  return Number(value.toFixed(precision));
}

// ---------- units ----------
//
// OBD-II is metric on the wire without exception: temperatures are Celsius with
// a 40-degree offset, speed is km/h. Conversion is a display concern only, so
// it happens here and never touches the decoders or anything that gets stored.
// A scan recorded today still reads correctly if the preference changes later.

export type UnitSystem = 'metric' | 'imperial';

export interface DisplayValue {
  value: number;
  unit: string;
  precision: number;
}

export function convertVital(spec: PidSpec, value: number, units: UnitSystem): DisplayValue {
  const precision = spec.precision ?? (Number.isInteger(value) ? 0 : 1);
  if (units === 'metric') return { value, unit: spec.unit, precision };

  switch (spec.unit) {
    case '°C':
      return { value: Number((value * 1.8 + 32).toFixed(0)), unit: '°F', precision: 0 };
    case 'km/h':
      return { value: Number((value * 0.621371).toFixed(0)), unit: 'mph', precision: 0 };
    case 'km':
      return { value: Number((value * 0.621371).toFixed(0)), unit: 'mi', precision: 0 };
    default:
      // rpm, %, and volts are the same everywhere.
      return { value, unit: spec.unit, precision };
  }
}

// `units` is deliberately required. It used to default to metric, which meant a
// call site that forgot it silently rendered the wrong unit rather than failing
// to compile - which is exactly how the History detail view spent a while
// showing Celsius to someone who had chosen Fahrenheit.
export function formatVital(spec: PidSpec, value: number | null, units: UnitSystem): string {
  if (value === null) return '—';
  const d = convertVital(spec, value, units);
  return `${d.value.toLocaleString(undefined, { maximumFractionDigits: d.precision })} ${d.unit}`;
}
